import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material'
import { Receipt as InvoiceIcon } from '@mui/icons-material'

interface InvoiceConversionDialogProps {
  open: boolean
  salesOrderId: string
  salesOrderNumber: string
  onClose: () => void
  onSuccess: (invoiceId: string, invoiceNumber: string) => void
}

const InvoiceConversionDialog: React.FC<InvoiceConversionDialogProps> = ({
  open,
  salesOrderId,
  salesOrderNumber,
  onClose,
  onSuccess,
}) => {
  const [invoiceType, setInvoiceType] = useState<'GST' | 'NON_GST'>('GST')
  const [dueDays, setDueDays] = useState<number>(30)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleConvert = async () => {
    setLoading(true)
    setError(null)

    try {
      const { salesOrderService } = await import('../../services/salesOrderService')
      
      const result = await salesOrderService.convertToInvoice(salesOrderId, dueDays)
      
      onSuccess(result.invoice_id, result.invoice_number)
      onClose()
    } catch (err: any) {
      console.error('Invoice conversion error:', err)
      setError(
        err.response?.data?.error?.message ||
        err.response?.data?.detail ||
        'Failed to convert sales order to invoice. Please try again.'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      setError(null)
      onClose()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <InvoiceIcon color="primary" />
        <Typography variant="h6" component="span">
          Convert to Invoice
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Converting Sales Order <strong>{salesOrderNumber}</strong> to an invoice.
            This will create a new invoice and update the sales order status.
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
              Invoice Type
            </FormLabel>
            <RadioGroup
              value={invoiceType}
              onChange={(e) => setInvoiceType(e.target.value as 'GST' | 'NON_GST')}
            >
              <FormControlLabel
                value="GST"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      GST Invoice
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Invoice with GST calculations (CGST/SGST/IGST)
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="NON_GST"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      Non-GST Invoice
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Simplified invoice without GST details
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>

          <TextField
            fullWidth
            type="number"
            label="Payment Due Days"
            value={dueDays}
            onChange={(e) => setDueDays(Math.max(1, Math.min(365, parseInt(e.target.value) || 30)))}
            helperText="Number of days until payment is due (1-365 days)"
            InputProps={{
              inputProps: { min: 1, max: 365 }
            }}
            sx={{ mb: 2 }}
          />

          <Typography variant="body2" color="text.secondary">
            The invoice will be created with all items from the sales order.
            The sales order status will be updated to "Invoiced".
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConvert}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <InvoiceIcon />}
          sx={{ textTransform: 'none', fontWeight: 500 }}
        >
          {loading ? 'Converting...' : 'Convert to Invoice'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default InvoiceConversionDialog
